import React, { useState, useEffect } from 'react';
import { ExternalLink, Play, MousePointer } from 'lucide-react';

const AdMarketplace = () => {
  const [marketplace, setMarketplace] = useState(null);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadMarketplace();
  }, []);

  const loadMarketplace = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/public/ad-marketplace`);
      const data = await response.json();
      if (data.ok) {
        setMarketplace(data.marketplace);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading marketplace:', error);
      setLoading(false);
    }
  };

  const recordImpression = async (adId) => {
    try {
      await fetch(`${backendUrl}/api/public/ad-impression/${adId}`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error recording impression:', error);
    }
  };

  const recordClick = async (adId, targetUrl) => {
    try {
      await fetch(`${backendUrl}/api/public/ad-click/${adId}`, {
        method: 'POST'
      });
      window.open(targetUrl, '_blank');
    } catch (error) {
      console.error('Error recording click:', error);
    }
  };

  useEffect(() => {
    // Record impressions for all visible ads
    if (marketplace) {
      if (marketplace.primary) recordImpression(marketplace.primary.id);
      marketplace.video_ads?.forEach(ad => recordImpression(ad.id));
      marketplace.banner_large?.forEach(ad => recordImpression(ad.id));
      marketplace.banner_medium?.forEach(ad => recordImpression(ad.id));
      marketplace.banner_small?.forEach(ad => recordImpression(ad.id));
      marketplace.text_ads?.forEach(ad => recordImpression(ad.id));
    }
  }, [marketplace]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Marketplace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">📢 BBC Quick List Marketplace</h1>
          <p className="text-xl text-blue-100">
            Discover opportunities, products, and services from our network
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Primary Featured Ad */}
        {marketplace?.primary && (
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden border-4 border-yellow-400">
            <div className="bg-yellow-400 px-4 py-2 flex items-center justify-center gap-2">
              <span className="text-xl">⭐</span>
              <span className="font-bold text-gray-900">FEATURED SPOTLIGHT</span>
              <span className="text-xl">⭐</span>
            </div>
            <div 
              className="p-8 cursor-pointer hover:bg-gray-50 transition"
              onClick={() => recordClick(marketplace.primary.id, marketplace.primary.target_url)}
            >
              {marketplace.primary.video_url ? (
                <div className="aspect-video">
                  <iframe
                    src={marketplace.primary.video_url}
                    className="w-full h-full rounded-lg"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <>
                  <h2 className="text-3xl font-bold mb-4">{marketplace.primary.title}</h2>
                  <p className="text-xl text-gray-700 mb-6">{marketplace.primary.description}</p>
                  <button className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 flex items-center gap-2">
                    Learn More <ExternalLink className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Video Ads Section */}
        {marketplace?.video_ads && marketplace.video_ads.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <Play className="w-6 h-6 text-red-600" />
              <h2 className="text-2xl font-bold">Featured Videos</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {marketplace.video_ads.map((ad) => (
                <div
                  key={ad.id}
                  className="border rounded-lg overflow-hidden hover:shadow-xl transition cursor-pointer"
                  onClick={() => recordClick(ad.id, ad.target_url)}
                >
                  <div className="aspect-video bg-gray-900">
                    {ad.video_url ? (
                      <iframe
                        src={ad.video_url}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-16 h-16 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold mb-2">{ad.title}</h3>
                    <p className="text-sm text-gray-600">{ad.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Large Banners (Desktop Leaderboard 728x90) */}
        {marketplace?.banner_large && marketplace.banner_large.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold mb-4">Featured Banners</h3>
            <div className="space-y-4">
              {marketplace.banner_large.map((ad) => (
                <div
                  key={ad.id}
                  className="flex justify-center cursor-pointer hover:opacity-90 transition"
                  onClick={() => recordClick(ad.id, ad.target_url)}
                >
                  {ad.banner_url ? (
                    <img
                      src={ad.banner_url}
                      alt={ad.title}
                      className="max-w-full h-auto rounded-lg shadow-md"
                      style={{ maxWidth: '728px', height: 'auto' }}
                    />
                  ) : (
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg flex items-center justify-center"
                         style={{ width: '728px', height: '90px' }}>
                      <div className="text-center">
                        <p className="font-bold text-lg">{ad.title}</p>
                        <p className="text-sm">{ad.description}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Medium Banners (300x250) */}
        {marketplace?.banner_medium && marketplace.banner_medium.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold mb-4">Discover More</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {marketplace.banner_medium.map((ad) => (
                <div
                  key={ad.id}
                  className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-xl transition"
                  onClick={() => recordClick(ad.id, ad.target_url)}
                >
                  {ad.banner_url ? (
                    <img
                      src={ad.banner_url}
                      alt={ad.title}
                      className="w-full h-auto"
                    />
                  ) : (
                    <div className="bg-gradient-to-br from-green-500 to-blue-500 text-white p-6 flex items-center justify-center"
                         style={{ height: '250px' }}>
                      <div className="text-center">
                        <p className="font-bold text-lg mb-2">{ad.title}</p>
                        <p className="text-sm">{ad.description}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mobile Banners (320x50) */}
        {marketplace?.banner_small && marketplace.banner_small.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 md:hidden">
            <h3 className="text-xl font-bold mb-4">Quick Links</h3>
            <div className="space-y-3">
              {marketplace.banner_small.map((ad) => (
                <div
                  key={ad.id}
                  className="cursor-pointer hover:opacity-90 transition"
                  onClick={() => recordClick(ad.id, ad.target_url)}
                >
                  {ad.banner_url ? (
                    <img
                      src={ad.banner_url}
                      alt={ad.title}
                      className="w-full h-auto rounded-lg shadow-sm"
                    />
                  ) : (
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 rounded-lg flex items-center justify-between"
                         style={{ height: '50px' }}>
                      <span className="font-semibold text-sm">{ad.title}</span>
                      <ExternalLink className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Text Ads */}
        {marketplace?.text_ads && marketplace.text_ads.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <MousePointer className="w-5 h-5 text-blue-600" />
              <h3 className="text-xl font-bold">Quick Opportunities</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {marketplace.text_ads.map((ad) => (
                <div
                  key={ad.id}
                  className="border-l-4 border-blue-600 pl-4 py-2 cursor-pointer hover:bg-blue-50 transition"
                  onClick={() => recordClick(ad.id, ad.target_url)}
                >
                  <p className="font-semibold text-blue-900">{ad.text_line1}</p>
                  <p className="text-sm text-gray-700">{ad.text_line2}</p>
                  <p className="text-sm text-gray-600">{ad.text_line3}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl p-8 text-center shadow-xl">
          <h2 className="text-3xl font-bold mb-4">Want Your Ad Here?</h2>
          <p className="text-xl mb-6">
            Join BBC Quick List and get access to our advertising marketplace!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.href = '/join'}
              className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              Join Now
            </button>
            <button
              onClick={() => window.location.href = '/login'}
              className="bg-purple-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-900 transition"
            >
              Member Login
            </button>
          </div>
        </div>

        {/* Footer Info */}
        <div className="text-center text-gray-600 text-sm">
          <p>All advertisements are member-submitted and subject to approval.</p>
          <p className="mt-2">BBC Quick List is not responsible for advertiser claims or offers.</p>
        </div>
      </div>
    </div>
  );
};

export default AdMarketplace;
